import { useCallback } from "react";
import { useContext, useState } from "react";
import {
    Select,
    FormControl,
    InputLabel,
    MenuItem,
    Autocomplete,
    Paper,
    IconButton,
    TextField,
    Grid,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import bundeslaender from "../assets/bundeslaender.json";
import ResultLayer from "./ResultLayer";
import useWindowSize from "../hooks/useWindowSize";
import { SearchContext } from "../context/SearchContext";
import CoordinateSearch from "./CoordinateSearch";
import { MapPointContext } from "../context/MapPointContext";

const themes = ["FLIK-Nummer", "PLZ", "Bundesland", "Koordinaten"];
const laender = [
    "Baden-Württemberg",
    "Bayern",
    "Berlin",
    "Brandenburg",
    "Bremen",
    "Hessen",
    "Hamburg",
    "Mecklenburg-Vorpommern",
    "Niedersachsen",
    "Nordrhein-Westfalen",
    "Rheinland-Pfalz",
    "Sachsen",
    "Sachsen-Anhalt",
    "Saarland",
    "Schleswig-Holstein",
    "Thüringen",
];

export function SearchBar(props) {
    const searchContext = useContext(SearchContext);
    const mapPointContext = useContext(MapPointContext);
    const [searchWord, setSearchWord] = useState("");
    const [suggestion, setSuggestion] = useState([]);
    //const [errorMessage, setErrorMessage] = useState();
    const size = useWindowSize();
    const isDesktopHeader = size.width > 900;
    const isMobileCoordinateSearch =
        !isDesktopHeader && searchContext.searchTheme === "Koordinaten";

    // get PLZ Suggestions
    const getSuggestions = useCallback(() => {
        if (searchContext.searchTheme === "PLZ" && searchWord?.length > 1) {
            searchContext.search.plz.suggest(searchWord, (data) => {
                var plzValues = [];
                data = data.filter((v) => {
                    var exp = plzValues.indexOf(v[0]) === -1;
                    plzValues.push(v[0]);
                    return exp;
                });
                setSuggestion(
                    data.map((item) => ({
                        label: item[0] + ", " + item[1],
                        value: item[0],
                    })),
                );
            });
        }
    }, [searchContext.searchTheme, searchWord, searchContext.search]);

    //Event handlers

    const handleChangeSelectedOption = (event, newValue) => {
        if (
            ["mouse", "touch"].indexOf(event?.nativeEvent?.pointerType) !== -1
        ) {
            if (newValue.indexOf(", ") !== -1) {
                newValue = newValue.split(",")[0];
            }
            search(newValue);
        } else {
            setSearchWord(newValue);
        }
    };

    const handleInputChange = (event) => {
        setSearchWord(event.target.value);
    };

    // get Query Results
    const search = useCallback(
        (searchWord) => {
            const errorCallback = (error) => {
                if (error.status === 404) {
                    props.setErrorMessage(
                        searchContext.searchTheme === "PLZ"
                            ? "PLZ nicht gefunden."
                            : "FLIK-Nummer wurde nicht gefunden. Die gesuchte FLIK-Nummer wurde möglicherweise zu einem späteren Zeitpunkt, z.B. im Rahmen eines Flurbereinigungsverfahrens neu vergeben. Bitte versuchen Sie, die gewünschte Fläche über den Kartenausschnitt zu identifizieren.",
                    );
                }
            };
            props.setErrorMessage(undefined);
            if (searchContext.searchTheme === "Bundesland") {
                let _results = bundeslaender.features.filter((item) => {
                    return item.properties.name === searchWord;
                });
                if (typeof _results[0] !== "undefined") {
                    searchContext.setSearchResult(_results);
                }
            }
            var _searchCallback = (data) => {
                if (data) {
                    searchContext.setSearchResult(data);
                    setSearchWord("");
                    setSuggestion([]);
                }
            };

            if (searchContext.searchTheme === "FLIK-Nummer") {
                let formated_searchWord = searchWord.replace(
                    /[^a-zA-Z0-9]/g,
                    "",
                );
                if (!formated_searchWord.startsWith("DE")) {
                    formated_searchWord = "DE" + formated_searchWord;
                }

                searchContext.search.flik.search(
                    formated_searchWord,
                    _searchCallback,
                    errorCallback,
                );
            }
            if (searchContext.searchTheme === "PLZ") {
                searchContext.search.plz.search(
                    searchWord,
                    _searchCallback,
                    errorCallback,
                );
            }
        },
        [props, searchContext],
    );

    const handleEnterkey = (event) => {
        if (event.key === "Enter") {
            search(event.target.value);
        }
    };

    const handleSubmit = useCallback(
        (event) => {
            event.preventDefault();
            props.setErrorMessage(undefined);
            search(searchWord);
        },
        [searchWord, search, props],
    );

    

    return (
        <>
            <form
                style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                }}
            >
                <Paper
                    style={{
                        margin: isDesktopHeader ? "0" : "5px 0",
                        width: "100%",
                        maxWidth: isDesktopHeader ? "680px" : "540px",
                        alignItems: "center",
                        display: "flex",
                    }}
                >
                    <Grid
                        container
                        style={{
                            paddingLeft: "13px",
                            paddingRight: "13px",
                            alignItems:
                                searchContext.searchTheme === "FLIK-Nummer"
                                    ? "flex-end"
                                    : "center",
                            flexWrap: isMobileCoordinateSearch
                                ? "wrap"
                                : "nowrap",
                            width: "100%",
                        }}
                    >
                        <Grid
                            size={
                                isMobileCoordinateSearch
                                    ? 12
                                    : isDesktopHeader
                                      ? 3
                                      : 4
                            }
                            sx={{ flexShrink: 0 }}
                        >
                            <FormControl
                                sx={{
                                    m: isDesktopHeader ? 0.5 : 0,
                                    width: isMobileCoordinateSearch
                                        ? "100%"
                                        : size.width > 600
                                          ? "140px"
                                          : "104px",
                                }}
                            >
                                <InputLabel
                                    variant="standard"
                                    id="search-type-select-helper-label"
                                >
                                    Suchtyp
                                </InputLabel>
                                <Select
                                    options={themes}
                                    variant="standard"
                                    value={searchContext.searchTheme}
                                    label="Suchtyp"
                                    labelId="search-type-select-helper-label"
                                    onChange={(e) => {
                                        const nextTheme = e.target.value;

                                        props.setErrorMessage(undefined);
                                        searchContext.setSearchTheme(nextTheme);

                                        // Keep GPS point so CoordinateSearch can preload it
                                        if (nextTheme !== "Koordinaten") {
                                            mapPointContext.clearActivePoint();
                                        }

                                        setSearchWord("");
                                    }}
                                >
                                    {themes.map((el) => (
                                        <MenuItem key={el} value={el}>
                                            {el}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {searchContext.searchTheme === "" && (
                            <Grid
                                size={isDesktopHeader ? 8 : 6}
                                sx={{ minWidth: 0 }}
                            ></Grid>
                        )}
                        {searchContext.searchTheme === "FLIK-Nummer" && (
                            <>
                                <Grid
                                    size={isDesktopHeader ? 8 : 5}
                                    sx={{ minWidth: 0 }}
                                >
                                    <TextField
                                        variant="standard"
                                        sx={{
                                            width: "100%",
                                            minWidth: 0,
                                        }}
                                        id="inputFLIK"
                                        placeholder="Geben Sie eine Nummer ein."
                                        value={searchWord}
                                        onChange={handleInputChange}
                                    />
                                </Grid>
                            </>
                        )}

                        {searchContext.searchTheme === "PLZ" && (
                            <>
                                <Grid
                                    size={isDesktopHeader ? 8 : 5}
                                    sx={{ minWidth: 0 }}
                                >
                                    <Autocomplete
                                        options={suggestion}
                                        value={searchWord}
                                        renderInput={(params) => (
                                            <TextField
                                                variant="standard"
                                                label="Suchbegriff"
                                                {...params}
                                            />
                                        )}
                                        onInputChange={
                                            handleChangeSelectedOption
                                        }
                                        //onSelect={handleDirectSearch}
                                        onKeyDown={handleEnterkey}
                                        isOptionEqualToValue={(option, value) =>
                                            option?.value === value
                                        }
                                        onSelect={(e) => {
                                            getSuggestions();
                                        }}
                                        onChange={handleEnterkey}
                                        sx={{
                                            width: "100%",
                                            minWidth: 0,
                                        }}
                                    />
                                </Grid>
                            </>
                        )}
                        {searchContext.searchTheme === "Bundesland" && (
                            <>
                                <Grid
                                    size={isDesktopHeader ? 8 : 4}
                                    sx={{ minWidth: 0 }}
                                >
                                    <Autocomplete
                                        options={laender}
                                        value={searchWord}
                                        renderInput={(params) => (
                                            <TextField
                                                variant="standard"
                                                label="Suchbegriff"
                                                {...params}
                                                onKeyDown={handleEnterkey}
                                            />
                                        )}
                                        onInputChange={
                                            handleChangeSelectedOption
                                        }
                                        sx={{
                                            width: "100%",
                                            minWidth: 0,
                                        }}
                                    />
                                </Grid>
                            </>
                        )}
                        {searchContext.searchTheme === "Koordinaten" && (
                            <Grid
                                size={
                                    isMobileCoordinateSearch
                                        ? 12
                                        : isDesktopHeader
                                          ? 8
                                          : 8
                                }
                                sx={{ minWidth: 0 }}
                            >
                                <CoordinateSearch
                                    mapId="map_1"
                                    setErrorMessage={props.setErrorMessage}
                                    isMobileLayout={isMobileCoordinateSearch}
                                />
                            </Grid>
                        )}
                        {!["Bundesland", "Koordinaten"].includes(
                            searchContext.searchTheme,
                        ) && (
                            <Grid size="auto" sx={{ flexShrink: 0 }}>
                                <IconButton
                                    aria-label="search"
                                    onClick={handleSubmit}
                                    type="submit"
                                >
                                    <SearchIcon style={{ color: "black" }} />
                                </IconButton>
                            </Grid>
                        )}
                    </Grid>
                </Paper>
            </form>

            {searchContext.searchResult?.[0] && (
                <ResultLayer json={searchContext.searchResult} />
            )}
        </>
    );
}
